import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, Paper, LinearProgress } from '@material-ui/core';
import { Storage } from 'aws-amplify';
import API from '../../api-graphql';
import { useTranslation } from 'react-i18next';
import AlertDialog from '../UI/AlertDialog';
import UploadForm from '../UploadForm';
import { connect } from 'react-redux';
import { CONSTANTS } from '../../constants/constants';

const useStyles = makeStyles(theme => ({
  paper: {
    maxWidth: '50%',
    margin: 'auto',
    padding: theme.spacing(2),
    [theme.breakpoints.up(600 + theme.spacing(6))]: {
      marginTop: theme.spacing(6),
      marginBottom: theme.spacing(6),
      padding: theme.spacing(3)
    },
    [theme.breakpoints.down(600 + theme.spacing(6))]: {
      maxWidth: '100%'
    }
  }
}));

function getInitialState (user, group) {
  const timestamp = Date.now();
  const id = `${timestamp}_${user.username}`;
  return {
    group,
    id,
    fileUrl: `document/${group}/${id}/${id}`
  };
}

const DocumentUploadView = (props) => {
  const { newDocument, history, user, group, subgroup } = props;
  const classes = useStyles();
  const { t } = useTranslation();
  const [input] = useState(() => getInitialState(user, group));
  const [files, setFiles] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const FILE_TYPES_ACCEPTED = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.ppt', '.pptx', '.odt', '.ods', '.odp'];

  function generatePathS3 (folder, ext, user, group) {
    const timestamp = Date.now();
    const usernameNormalized = user.username
      .replace(' ', '_')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '');

    const id = `${timestamp}_${usernameNormalized}`;
    return `document/${group}/${folder}/${id}${ext}`;
  }

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      setLoading(true);
      for (let index = 0; index < files.length; index++) {
        const file = files[index];

        const ext = /[.]\w{3,4}$/.exec(file.name)[0];

        if (!ext || !FILE_TYPES_ACCEPTED.includes(ext)) {
          const error = new Error('Wrong Extension');
          error.customMessage = `${t('docUpdate:extensionError')} ${FILE_TYPES_ACCEPTED.join(', ')}`;
          throw error;
        }

        if (index === 0) {
          input.name = e.target.name.value;
          input.description = e.target.description.value || '';
          input.status = CONSTANTS.DOCUMENT.STATUS.INIT;
          input.data = '';
          input.createdAt = new Date().toISOString();
          input.updatedAt = new Date().toISOString();
          input.type = 'document';
          input.fileUrl += ext;
          input.annexes = [];
          input.principal = { name: file.name, path: input.fileUrl, description: input.description };
          await Storage.put(input.fileUrl, file);
        } else {
          const path = generatePathS3(input.id, ext, user, group);

          const annexe = { name: file.name, path };
          await Storage.put(path, file);
          input.annexes.push(annexe);
        }
      }

      const createdDocument = await API.createDocument(input, subgroup);
      const documentIdCreated = createdDocument.data.createDocument.id;
      props.history.push(`/document/detail/${documentIdCreated}`);
    } catch (e) {
      console.error('Error creating PDF', e);
      setLoading(false);
      setError(e.customMessage || t('templateUploadView:genericError'));
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 0, position: 'absolute', left: 0, width: '100%', zIndex: 9999 }}>
        <LinearProgress />
      </div>
    );
  }

  return (
    <Paper className={classes.paper}>
      <AlertDialog open={!!error} title='Error' description={error} setOpen={() => setError(null)} />
      <UploadForm
        newDocument={newDocument}
        handleSubmit={handleSubmit}
        handleCancel={history.goBack}
        setContent={setFiles}
        multiple
        accept={FILE_TYPES_ACCEPTED.join(',')}
        title={t('docUpload:createNewDoc')}
      />
    </Paper>
  );
};

DocumentUploadView.propTypes = {
  history: PropTypes.object.isRequired,
  newDocument: PropTypes.bool
};

const mapStateToProps = (state) => {
  return {
    user: state.config.user,
    group: state.config.group,
    subgroup: state.config.subgroup
  };
};

DocumentUploadView.propTypes = {
  user: PropTypes.object.isRequired,
  group: PropTypes.string.isRequired,
  subgroup: PropTypes.string.isRequired
};

export default connect(mapStateToProps, null)(DocumentUploadView);
